/**
 * flatoo wishlist analytics
 *
 * Tracks transitions in the Halo theme's localStorage-based wishlist.
 * Sends:
 * - GTM dataLayer event: add_to_wishlist / remove_from_wishlist
 * - Shopify custom analytics event: flatoo_wishlist_added / flatoo_wishlist_removed
 * - Direct gtag event when a GA4 measurement ID can be detected on the page
 */
(() => {
    'use strict';

    const CACHE = new Map();
    const WISHLIST_KEY = 'wishlistItem';
    const DEBUG = new URLSearchParams(window.location.search).get('wishlist_debug') === '1';

    const debugLog = (...args) => {
        if (DEBUG && window.console) {
            console.log('[flatoo wishlist GA4]', ...args);
        }
    };

    const readWishlist = () => {
        try {
            const stored = window.localStorage.getItem(WISHLIST_KEY);
            const parsed = stored ? JSON.parse(stored) : [];
            return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
            debugLog('Could not read wishlist.', error);
            return [];
        }
    };

    const normalizeRoot = () => {
        const root = window.routes && window.routes.root ? window.routes.root : '/';
        return root.endsWith('/') ? root : `${root}/`;
    };

    const getProduct = async (handle) => {
        if (!handle) return null;
        if (CACHE.has(handle)) return CACHE.get(handle);

        const request = window.fetch(`${normalizeRoot()}products/${encodeURIComponent(handle)}.js`, {
            credentials: 'same-origin',
            headers: {
                Accept: 'application/json'
            }
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Product request failed: ${response.status}`);
                }
                return response.json();
            })
            .catch((error) => {
                debugLog('Could not retrieve product data.', error);
                return null;
            });

        CACHE.set(handle, request);
        return request;
    };

    const getPageType = () => {
        const bodyClass = document.body ? document.body.className : '';
        const match = bodyClass.match(/(?:^|\s)template-([^\s]+)/);
        if (match && match[1]) return match[1];

        const path = window.location.pathname;
        if (/^\/products\//.test(path)) return 'product';
        if (/^\/collections\//.test(path)) return 'collection';
        if (/^\/search/.test(path)) return 'search';
        if (/^\/pages\//.test(path)) return 'page';
        return path === '/' ? 'index' : 'other';
    };

    const getWishlistLocation = (button) => {
        if (button.closest('[data-wishlist-content]')) return 'wishlist_page';
        if (button.closest('.halo-quick-view, .halo-popup, [data-quick-view-popup]')) return 'quick_view';
        if (button.closest('.sticky-add-to-cart, .halo-sticky-add-to-cart, [data-sticky-add-to-cart]')) return 'sticky_add_to_cart';
        if (button.closest('.productView')) return 'product_page';

        if (button.closest('.card')) {
            const pageType = getPageType();
            if (pageType === 'product') return 'related_product_card';
            if (pageType === 'collection') return 'collection_product_card';
            if (pageType === 'search') return 'search_product_card';
            if (pageType === 'index') return 'home_product_card';
            return 'product_card';
        }

        return getPageType();
    };

    const getSelectedVariantId = (button) => {
        const productScope = button.closest('.productView, .product, [data-product]');
        if (!productScope) return null;

        const checked = productScope.querySelector('input[name="id"]:checked');
        const variantInput = checked || productScope.querySelector('input[name="id"], select[name="id"]');
        if (!variantInput || !variantInput.value) return null;

        const parsed = Number(variantInput.value);
        return Number.isFinite(parsed) ? parsed : null;
    };

    const getCurrency = () => {
        return (
            (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
            (window.Shopify && window.Shopify.currency && window.Shopify.currency.current) ||
            'JPY'
        );
    };

    const getPrice = (product, variant) => {
        const rawPrice = variant && typeof variant.price === 'number'
            ? variant.price
            : product && typeof product.price === 'number'
                ? product.price
                : 0;
        return Number((rawPrice / 100).toFixed(2));
    };

    const buildPayload = (eventName, button, handle, product, wishlistCount) => {
        const selectedVariantId = getSelectedVariantId(button);
        const variants = product && Array.isArray(product.variants) ? product.variants : [];
        const variant = variants.find((item) => Number(item.id) === selectedVariantId) || variants[0] || null;
        const price = getPrice(product, variant);
        const currency = getCurrency();
        const fallbackId = button.dataset.productId || handle;
        const productId = product && product.id ? String(product.id) : String(fallbackId || handle);
        const itemName = product && product.title ? product.title : handle;
        const sku = variant && variant.sku ? variant.sku : '';

        return {
            event: eventName,
            ecommerce: {
                currency,
                value: price,
                items: [
                    {
                        item_id: productId,
                        item_name: itemName,
                        item_brand: product && product.vendor ? product.vendor : '',
                        item_category: product && product.type ? product.type : '',
                        item_variant: variant && variant.title !== 'Default Title' ? variant.title : '',
                        item_variant_id: variant && variant.id ? String(variant.id) : '',
                        item_sku: sku,
                        price,
                        quantity: 1
                    }
                ]
            },
            product_handle: handle,
            wishlist_location: getWishlistLocation(button),
            wishlist_count: wishlistCount,
            page_type: getPageType(),
            page_path: window.location.pathname,
            tracking_source: 'flatoo_theme_wishlist_v1'
        };
    };

    const findGa4MeasurementId = () => {
        const script = document.querySelector('script[src*="googletagmanager.com/gtag/js?id=G-"]');
        if (script && script.src) {
            const match = script.src.match(/[?&]id=(G-[A-Z0-9]+)/i);
            if (match) return match[1];
        }

        if (Array.isArray(window.dataLayer)) {
            for (const entry of window.dataLayer) {
                if (!entry || typeof entry.length !== 'number') continue;
                if (entry[0] === 'config' && /^G-[A-Z0-9]+$/i.test(entry[1])) {
                    return entry[1];
                }
            }
        }

        return '';
    };

    const sendToAnalytics = (payload) => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(payload);

        const ga4MeasurementId = findGa4MeasurementId();
        if (ga4MeasurementId) {
            window.gtag = window.gtag || function gtag() {
                window.dataLayer.push(arguments);
            };

            const directParams = {
                ...payload.ecommerce,
                product_handle: payload.product_handle,
                wishlist_location: payload.wishlist_location,
                wishlist_count: payload.wishlist_count,
                page_type: payload.page_type,
                page_path: payload.page_path,
                tracking_source: payload.tracking_source,
                send_to: ga4MeasurementId
            };
            window.gtag('event', payload.event, directParams);
        }

        if (
            window.Shopify &&
            window.Shopify.analytics &&
            typeof window.Shopify.analytics.publish === 'function'
        ) {
            const shopifyEventName = payload.event === 'add_to_wishlist'
                ? 'flatoo_wishlist_added'
                : 'flatoo_wishlist_removed';

            try {
                window.Shopify.analytics.publish(shopifyEventName, {
                    ecommerce: payload.ecommerce,
                    product_handle: payload.product_handle,
                    wishlist_location: payload.wishlist_location,
                    wishlist_count: payload.wishlist_count,
                    page_type: payload.page_type,
                    page_path: payload.page_path,
                    tracking_source: payload.tracking_source
                });
            } catch (error) {
                debugLog('Shopify analytics publish failed.', error);
            }
        }

        debugLog('Sent event.', payload, { ga4MeasurementId });
    };

    const trackWishlistTransition = async (eventName, button, handle, wishlistCount) => {
        const product = await getProduct(handle);
        const payload = buildPayload(eventName, button, handle, product, wishlistCount);
        sendToAnalytics(payload);
    };

    document.addEventListener('click', (event) => {
        const button = event.target.closest('[data-wishlist]');
        if (!button) return;

        const handle = button.dataset.wishlistHandle || button.dataset.productHandle;
        if (!handle) {
            debugLog('Wishlist button does not have a product handle.', button);
            return;
        }

        const beforeList = readWishlist();
        const wasAdded = beforeList.includes(handle) || button.classList.contains('wishlist-added');

        window.setTimeout(() => {
            const afterList = readWishlist();
            const isAdded = afterList.includes(handle);

            if (wasAdded === isAdded) {
                debugLog('No wishlist state transition detected.', { handle, wasAdded, isAdded });
                return;
            }

            const eventName = isAdded ? 'add_to_wishlist' : 'remove_from_wishlist';
            trackWishlistTransition(eventName, button, handle, afterList.length);
        }, 80);
    }, true);
})();
